import { ContextSelectedOptions } from "@appretio/context-selector";
import React from "react";
import {
  chartDualContainer,
  chartDualItem
} from "../../../components/charts/Chart.css";
import { PriceDilutionChart } from "../../../components/charts/price/PriceDilutionChart";
import { RrpKPIsChart } from "../../../components/charts/price/RrpKPIsChart";
import { TemporalContext } from "../../../components/temporalContextSelector/data/temporalContextOptions";
import { useFetchPriceDilution } from "../hooks/useFetchPriceDilution";
import { ReviewDataResponse } from "../hooks/useFetchReviewData";
import { useFetchRrpKPIs } from "../hooks/useFetchRrpKPIs";

interface PriceTabProps {
  appliedFilters: {
    contextOptions: ContextSelectedOptions;
    temporalOptions: TemporalContext;
  };
  hasUserApplied: boolean;
  selectedRowData: ReviewDataResponse;
}

export const PriceTab = React.memo(function PriceTab({
  appliedFilters,
  hasUserApplied,
  selectedRowData
}: PriceTabProps) {
  // Only fetch when this tab is rendered
  const { data: rrpKPIsData, isFetching } = useFetchRrpKPIs(
    appliedFilters.contextOptions,
    appliedFilters.temporalOptions,
    hasUserApplied
  );

  // Only fetch when this tab is rendered
  const { data: priceDilutionData, isFetching: isPriceDilutionFetching } =
    useFetchPriceDilution(
      appliedFilters.contextOptions,
      appliedFilters.temporalOptions,
      hasUserApplied
    );

  // Filter data based on selected rows
  const filteredRrpKPIsData = React.useMemo(() => {
    if (!selectedRowData.length || !rrpKPIsData) return rrpKPIsData;

    return rrpKPIsData.filter((kpi) =>
      selectedRowData.some(
        (row) =>
          row.style_recommended_retail_price_eur ===
          kpi.style_recommended_retail_price_eur
      )
    );
  }, [selectedRowData, rrpKPIsData]);

  const filteredPriceDilutionData = React.useMemo(() => {
    if (!selectedRowData.length || !priceDilutionData) return priceDilutionData;

    return priceDilutionData.filter((dataRow) =>
      selectedRowData.some(
        (row) =>
          row.style_recommended_retail_price_eur ===
          dataRow.style_recommended_retail_price_eur
      )
    );
  }, [selectedRowData, priceDilutionData]);

  return (
    <div className={chartDualContainer}>
      <div className={chartDualItem}>
        <RrpKPIsChart
          rrpKPIsData={filteredRrpKPIsData ?? []}
          isLoading={isFetching}
        />
      </div>
      <div className={chartDualItem}>
        <PriceDilutionChart
          priceDilutionData={filteredPriceDilutionData ?? []}
          isLoading={isPriceDilutionFetching}
        />
      </div>
    </div>
  );
});
